import React, { useState, useEffect } from 'react';
import { Map, ZoomControl, Marker, GeoJson, Overlay } from 'pigeon-maps';

const toHHmm = (ts) =>
    ts ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '';

// Beacon ts can be epoch ms, ISO string, or pre-formatted "HH:mm" - handle all
const formatBeaconTime = (ts) => {
    if (ts == null || ts === '') return '';
    if (typeof ts === 'number' && Number.isFinite(ts)) return toHHmm(ts);
    if (typeof ts === 'string' && /^\d{1,2}:\d{2}(:\d{2})?$/.test(ts.trim())) return ts.trim();
    const d = new Date(ts);
    return Number.isFinite(d.getTime()) ? toHHmm(ts) : String(ts);
};

const getRowId = (row) => `geo-${row.latitude}-${row.longitude}-${row.timestamp}`;

// Catmull-Rom spline — interpolates a smooth curve that passes through every original point.
// coords: [lng, lat][] (GeoJSON order), steps: interpolated points per segment
const catmullRomPoint = (p0, p1, p2, p3, t) => {
    const t2 = t * t, t3 = t2 * t;
    return [
        0.5 * ((2 * p1[0]) + (-p0[0] + p2[0]) * t + (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 + (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3),
        0.5 * ((2 * p1[1]) + (-p0[1] + p2[1]) * t + (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 + (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3),
    ];
};

const catmullRomSmooth = (coords, steps = 12) => {
    if (!coords || coords.length < 2) return coords;
    const out = [];
    for (let i = 0; i < coords.length - 1; i++) {
        const p0 = coords[Math.max(i - 1, 0)];
        const p1 = coords[i];
        const p2 = coords[i + 1];
        const p3 = coords[Math.min(i + 2, coords.length - 1)];
        for (let s = 0; s < steps; s++) {
            out.push(catmullRomPoint(p0, p1, p2, p3, s / steps));
        }
    }
    out.push(coords[coords.length - 1]);
    return out;
};

// Applies Catmull-Rom smoothing to a GeoJSON FeatureCollection containing a LineString
const smoothLineString = (geoJson, steps = 12) => {
    if (!geoJson?.features?.length) return geoJson;
    return {
        ...geoJson,
        features: geoJson.features.map(f => {
            if (f.geometry?.type !== 'LineString') return f;
            return { ...f, geometry: { ...f.geometry, coordinates: catmullRomSmooth(f.geometry.coordinates, steps) } };
        }),
    };
};

const DEFAULT_CENTER = [53.3498, -6.2603];
const DEFAULT_ZOOM = 18;

const GEOFENCE_COLOR = '#0000ff';

const EmployeeLocationMap = ({
    selectedGeoCoords = [],
    pathGeoJson,
    beaconPathGeoJson,
    beaconPoints = [],
    showGeoLine = true,
    showBeaconLine = true,
    geofencesToShow = [],
    height = 500,
    title = 'Location Map',
    aboveMap,
    footer,
}) => {
    const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
    const [zoom, setZoom] = useState(DEFAULT_ZOOM);

    // Re-center when coords load or change (matches LiveLocationMap pattern)
    useEffect(() => {
        if (selectedGeoCoords?.length > 0) {
            const [lat, lng] = [selectedGeoCoords[0].latitude, selectedGeoCoords[0].longitude];
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
                setMapCenter([lat, lng]);
                setZoom(DEFAULT_ZOOM);
            }
        } else if (beaconPoints?.length > 0) {
            const p = beaconPoints[0];
            if (Number.isFinite(p.lat) && Number.isFinite(p.lng)) {
                setMapCenter([p.lat, p.lng]);
                setZoom(DEFAULT_ZOOM);
            }
        }
    }, [selectedGeoCoords, beaconPoints]);

    return (
        <div
            style={{
                border: '1px solid #e5e7eb',
                borderRadius: 12,
                overflow: 'hidden',
                backgroundColor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 16px',
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb',
                }}
            >
                <i className="fas fa-map-marker-alt" style={{ fontSize: 16, color: '#6b7280' }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{title}</span>
            </div>
            {aboveMap && aboveMap}
            <div
                style={{
                    height: `${height}px`,
                    width: '100%',
                    minWidth: 300,
                    borderRadius: 6,
                    overflow: 'hidden',
                }}
            >
                <Map
                    center={mapCenter}
                    zoom={zoom}
                    onBoundsChanged={({ center, zoom: z }) => {
                        setMapCenter(center);
                        setZoom(z);
                    }}
                    defaultZoom={DEFAULT_ZOOM}
                    maxZoom={19}
                    minZoom={5}
                >
                    <ZoomControl />

                    {/* Location geofence circles/polygons */}
                    {geofencesToShow?.map((gf, i) => (
                        <GeoJson
                            key={`geofence-${i}-${gf.title}`}
                            data={gf.fence}
                            styleCallback={() => ({
                                fill: `${GEOFENCE_COLOR}33`,
                                strokeWidth: 2,
                                stroke: GEOFENCE_COLOR,
                            })}
                        />
                    ))}

                    {/* Location geofence labels */}
                    {geofencesToShow?.map((gf, i) => {
                        const [lat, lng] = gf.anchor || [];
                        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                        return (
                            <Overlay key={`geofence-label-${i}-${gf.title}`} anchor={[lat, lng]} offset={[0, 0]}>
                                <div
                                    style={{
                                        transform: 'translate(-50%, -50%)',
                                        color: '#fff',
                                        textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
                                        fontSize: 11,
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {gf.title}
                                </div>
                            </Overlay>
                        );
                    })}

                    {/* Geo path line — smoothed with Catmull-Rom, markers stay on original coords */}
                    {showGeoLine && pathGeoJson?.features?.length > 0 && (
                        <GeoJson
                            data={smoothLineString(pathGeoJson)}
                            styleCallback={() => ({
                                strokeWidth: 4,
                                stroke: '#2563eb',
                                fill: 'none',
                            })}
                        />
                    )}

                    {/* Beacon path line — smoothed with Catmull-Rom */}
                    {showBeaconLine && beaconPathGeoJson?.features?.length > 0 && (
                        <GeoJson
                            data={smoothLineString(beaconPathGeoJson)}
                            styleCallback={() => ({
                                strokeWidth: 4,
                                stroke: '#ea580c',
                                fill: 'none',
                            })}
                        />
                    )}

                    {/* Geo markers */}
                    {selectedGeoCoords?.map((p, i) => (
                        <Marker key={`${getRowId(p)} + ${i}`} anchor={[p.latitude, p.longitude]} style={{ display: showGeoLine ? 'block' : 'none' }} />
                    ))}

                    {/* Beacon markers */}
                    {beaconPoints?.map((p, i) => (
                        <Marker
                            key={`beacon-${p.lat}-${p.lng}-${i}`}
                            anchor={[p.lat, p.lng]}
                            color="#ea580c"
                            style={{
                                display: showBeaconLine ? 'block' : 'none'
                            }}
                        />
                    ))}

                    {/* Geo timestamp labels */}
                    {selectedGeoCoords?.map((p, i) => {
                        const lat = Number(p.latitude);
                        const lng = Number(p.longitude);
                        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

                        return (
                            <Overlay key={`geo-ts-${i}`} anchor={[lat, lng]} offset={[0, 0]}>
                                <div
                                    style={{
                                        transform: 'translate(-50%, -140%)',
                                        background: 'rgba(17,24,39,0.85)',
                                        color: '#fff',
                                        padding: '2px 6px',
                                        borderRadius: 6,
                                        fontSize: 11,
                                        lineHeight: 1.2,
                                        whiteSpace: 'nowrap',
                                        pointerEvents: 'none',
                                        textAlign: 'center',
                                        display: showGeoLine ? 'block' : 'none',
                                    }}
                                >
                                    {toHHmm(p.timestamp)}
                                </div>
                            </Overlay>
                        );
                    })}

                    {/* Beacon timestamp labels */}
                    {beaconPoints?.map((p, i) => {
                        const lat = Number(p.lat);
                        const lng = Number(p.lng);
                        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                        return (
                            <Overlay key={`beacon-ts-${p.lat}-${p.lng}-${i}`} anchor={[lat, lng]} offset={[0, 0]}>
                                <div
                                    style={{
                                        transform: 'translate(-50%, -140%)',
                                        background: 'rgba(234,88,12,0.9)',
                                        color: '#fff',
                                        padding: '2px 6px',
                                        borderRadius: 6,
                                        fontSize: 11,
                                        lineHeight: 1.2,
                                        whiteSpace: 'nowrap',
                                        pointerEvents: 'none',
                                        textAlign: 'center',
                                        display: showBeaconLine ? 'block' : 'none',
                                    }}
                                >
                                    {formatBeaconTime(p.ts)}
                                </div>
                            </Overlay>
                        );
                    })}
                </Map>
            </div>
            {footer && footer}
        </div>
    );
};

export default EmployeeLocationMap;
